/**
 * 注文詳細 `/orders/{tenant_id}/{order_id}`（**D-88・FR-128**）＝A-03 の個別ルート。
 *
 * ★**なぜ独立したルートが要るか**：LINE の通知（受付/確定/出荷/配送/納品）の文面末尾に
 *   この URL を載せる（06 §7.1）。それまで A-03 は一覧しか無く、push から「その注文」へ
 *   着地する先が存在しなかった（＝通知を受けた職人が一覧から自分で探すことになる）。
 * ★**URL に tenant_id を持つ**＝子注文は接続ごとの API でしか引けない（テナント分離）。
 *   注文番号だけでは、どの卸に聞けばよいかがクライアントに分からない。
 * ★**接続が無い卸の注文は開けない**＝取引が終了した卸の注文は、卸側にしか残らない。
 *   404 を出すのではなく「なぜ見られないか」を書く（黙って空にしない）。
 *
 * ★**M1.9b（Design 忠実化）＝ここが「1件を読む」画面**（一覧はコンパクトなカードに徹する）。
 *   縦のレール（`os-rail`）で段・時刻・説明を上から読み、明細・送付先・金額・再注文を
 *   この画面に集約した（一覧から移設）。
 *   - 段の並びは**サーバの `progress_steps` のまま**（FR-107）＝短縮形に置き換えない。
 *   - 各段の時刻は `timeline` の**段名一致**で引く（無ければ「—」）。済み判定は `progress_index`。
 *   - 説明文は**画面が持つ表示専用の対応表**。未知の段名には説明を書かない（画面が状態を
 *     計算しない規律＝知らない段に勝手な意味をつけない）。
 */

import type { ConnectedTenant, PartnerOrder, partnerApi } from "@kenzai/api-client";
import { Badge, Card, ErrorNote, formatDate, formatDateTime, Money, Spinner } from "@kenzai/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { taxOf } from "./Cart";
import { CheckIcon, TruckIcon } from "./Orders";
import { useCart } from "../cart";
import { PageTitle } from "../Shell";

type Client = ReturnType<typeof partnerApi>;

/**
 * 段名 → その段で何が起きたかの一言（Design `sheetOrder` の d）。**表示専用**＝この表に
 * 無い段名は説明なしで描く（サーバが段を増やしても画面が嘘をつかない）。
 */
function stepNote(step: string, binName: string | null): string | null {
  const bin = binName ?? "配送便";
  switch (step) {
    case "受付済み":
      return "卸売業者に注文が届きました";
    case "確定":
      return "担当者が内容を確認しました";
    case "出荷済み":
      return `${bin}の積込対象になりました`;
    case "配送中":
      return `${bin}で現場へ向かっています`;
    case "納品済み":
      return "現場でお渡ししました";
    default:
      return null;
  }
}

export function OrderDetail({ client, connections }: { client: Client; connections: ConnectedTenant[] }) {
  const { tenantId, orderId } = useParams();
  const nav = useNavigate();
  const tid = Number(tenantId);
  const oid = Number(orderId);
  const connected = connections.find((c) => c.tenant_id === tid) ?? null;

  const q = useQuery({
    queryKey: ["partner-order", tid, oid],
    queryFn: () => client.order(tid, oid),
    // 接続が無ければ叩かない（403 を取りに行かない＝理由は画面が書く）
    enabled: connected !== null && connected.tenant_status !== "suspended" && Number.isFinite(tid) && Number.isFinite(oid),
  });

  return (
    <div style={{ display: "grid", gap: "var(--kz-space-3)" }} data-testid="order-detail">
      <PageTitle>ご注文の詳細</PageTitle>
      <button
        type="button"
        onClick={() => nav("/orders")}
        className="kz-pressable"
        style={{
          justifySelf: "start",
          minHeight: 44,
          border: "none",
          background: "transparent",
          font: "inherit",
          color: "var(--kz-color-primary)",
          cursor: "pointer",
          padding: 0,
        }}
      >
        ← 注文履歴へ
      </button>

      {connected === null ? (
        <Card>
          <p style={{ margin: 0 }}>
            このご注文は表示できません（お取引が終了した取引先のご注文です）。
            内容は、お取引先の建材卸売業者さまにお問い合わせください。
          </p>
        </Card>
      ) : connected.tenant_status === "suspended" ? (
        <Card>
          <p style={{ margin: 0 }}>
            {connected.tenant_name} は現在ご利用いただけません。お取引先にお問い合わせください。
          </p>
        </Card>
      ) : q.isLoading ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorNote error={q.error} />
      ) : (
        <OrderBody order={q.data!} client={client} />
      )}
    </div>
  );
}

function OrderBody({ order, client }: { order: PartnerOrder; client: Client }) {
  const cancelled = order.progress_index < 0;
  const today = order.is_arriving_today && !cancelled;
  // 段名 → 時刻（同じ段が2回ある場合は最初のもの＝最初にその段へ入った時刻）
  const at = new Map<string, string>();
  for (const t of order.timeline) if (!at.has(t.step)) at.set(t.step, t.at);

  const items = Number(order.total_amount ?? NaN);
  const fee = Number(order.delivery_fee ?? 0);
  // 単価が1行でも未確定なら金額は出さない（FR-82＝未確定を 0 として足さない）
  const subtotal = Number.isNaN(items) ? null : items + fee;
  const tax = subtotal === null ? null : taxOf(subtotal);

  return (
    <div>
      <div className="os-top">
        <span className="ord-sp">{order.tenant_name}</span>
        <span className="os-id kz-num">{order.display_no}</span>
      </div>

      {cancelled ? (
        <Card>
          <Badge tone="danger">取消</Badge>
          <p style={{ margin: "var(--kz-space-3) 0 0", fontSize: "var(--kz-font-size-sm)" }}>
            このご注文は取り消されています。ご不明な点は、お取引先の建材卸売業者さまにお問い合わせください。
          </p>
        </Card>
      ) : (
        <>
          <div className={`os-eta${today ? " transit" : ""}`}>
            <small>{today ? "本日お届け" : "お届け予定"}</small>
            <b data-testid={`eta-${order.order_id}`}>
              {today && <TruckIcon />}
              {formatDate(order.desired_date)}
              {order.bin_name && `・${order.bin_name}`}
            </b>
          </div>

          <div className="os-rail" data-testid={`progress-${order.order_id}`}>
            {order.progress_steps.map((step, i) => {
              const done = i <= order.progress_index;
              const time = at.get(step);
              const note = stepNote(step, order.bin_name);
              return (
                <div className={`os-step ${done ? "on" : "off"}`} key={step}>
                  <span className="os-mark">{done && <CheckIcon />}</span>
                  {i < order.progress_steps.length - 1 && <span className="os-line" />}
                  <div className="os-body">
                    <div className="os-h">
                      {/* 現在の段を機械で特定できるようにする（一覧のバーと同じ指し先） */}
                      <span data-testid={i === order.progress_index ? "progress-current" : undefined}>{step}</span>
                      <span className="os-at kz-num">{done && time ? formatDateTime(time) : "—"}</span>
                    </div>
                    {note && <div className="os-d">{done ? note : "これから"}</div>}
                    {/* ★「確定」だけは、待っているのが誰なのかを書く＝ここで止まって見えるのが
                        一番多く、卸の担当者の確認待ちだと分かれば問い合わせずに済む。 */}
                    {step === "確定" && !done && (
                      <div className="os-hint">卸売業者の担当者が確認すると、ここが「確定」に変わります。</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="ck-lbl">注文内容</div>
      <div className="ck-items">
        {order.items.map((i, n) => (
          <div className="ck-row" key={n}>
            <span>
              <b>{i.name}</b>
              <small>
                {i.qty === null ? (
                  "数量：確認中"
                ) : (
                  <>
                    <span className="kz-num">{Number(i.qty)}</span>
                    {i.unit}
                    {i.unit_price !== null && (
                      <>
                        {" ・ @"}
                        <span className="kz-num">{Number(i.unit_price).toLocaleString("ja-JP")}</span>円
                      </>
                    )}
                  </>
                )}
              </small>
            </span>
            <span className="ck-amt">
              <Money value={i.amount} />
            </span>
          </div>
        ))}
      </div>

      <div className="os-rows">
        <div className="dt-row">
          <span>送付先</span>
          <b>{order.site_name ?? "（現場の指定なし）"}</b>
        </div>
        <div className="dt-row">
          <span>お届け</span>
          <b>
            {formatDate(order.desired_date)}
            {order.bin_name && ` ${order.bin_name}`}
          </b>
        </div>
        {/* 配送料は**商品計と分けて出す**（FR-131・D-90）。null＝「込み」の卸＝行を出さない。 */}
        {order.delivery_fee !== null && (
          <div className="dt-row">
            <span>配送料</span>
            <b>
              <Money value={order.delivery_fee} />
            </b>
          </div>
        )}
        <div className="dt-row">
          <span>小計（税抜）</span>
          <b>
            <Money value={subtotal === null ? null : String(subtotal)} />
          </b>
        </div>
        <div className="dt-row">
          <span>消費税（10%）</span>
          <b>
            <Money value={tax === null ? null : String(tax)} />
          </b>
        </div>
        <div className="dt-row">
          <span>合計（税込）</span>
          <b>
            <Money value={subtotal === null || tax === null ? null : String(subtotal + tax)} />
          </b>
        </div>
      </div>

      {/* ★税額は**この画面の表示だけ**（注文のデータは税抜のまま）＝正式な税額は卸の請求書が正。 */}
      <div className="oa-foot">請求書は、これまでどおりお取引先の建材卸売業者さまから届きます。</div>

      <Reorder order={order} client={client} />
    </div>
  );
}

/**
 * FR-44改（D-54⑦）：同じ内容で再注文＝現在のカタログ・単価で再解決した明細をカートに載せ、
 * 「注文する」へ移動する（送信は既存フロー＝職人の明示操作）。廃番はカートに乗せず知らせる。
 *
 * ★M1.9b で**一覧カードからこの画面へ移した**＝一覧は「探す」ための場所で、1件ずつに
 *   取り返しのつく操作ボタンを並べると、目当ての注文を探す邪魔になる（1画面1件しか入らない）。
 */
function Reorder({ order, client }: { order: PartnerOrder; client: Client }) {
  const cart = useCart();
  const nav = useNavigate();
  const [note, setNote] = useState<string | null>(null);

  const reorder = useMutation({
    mutationFn: () => client.reorder(order.tenant_id, order.order_id),
    onSuccess: (res) => {
      if (res.items.length === 0) {
        // 乗せるものが無い＝カートは触らない（既存カートを消さない）。ここで知らせて留まる。
        setNote("この注文の商品は現在お取り扱いがありません。");
        return;
      }
      // ★**この卸のカートだけ**入れ替える（他の卸の品は消さない＝統合カートの前提）
      cart.clear(order.tenant_id);
      for (const it of res.items) {
        cart.add(order.tenant_id, {
          product_id: it.product_id,
          name: it.name,
          spec: it.spec,
          unit: it.unit,
          unit_price: it.unit_price,
          qty: Number(it.qty),
        });
      }
      if (res.unavailable.length > 0) {
        // ★廃番あり＝**自動遷移しない**（遷移するとこの通知も一緒に消える）。
        // カートには有効品だけ入れ、留まって知らせる＝職人が「注文する画面へ」で自分で進む。
        setNote(`取扱終了の ${res.unavailable.length} 品を除いてカートに入れました。「注文する画面へ」でご確認ください。`);
        return;
      }
      nav("/catalog"); // 全部有効＝そのまま「注文する」へ
    },
  });

  return (
    <>
      {note && (
        <div style={{ marginTop: "var(--kz-space-3)" }} data-testid={`reorder-note-${order.order_id}`}>
          <p style={{ margin: 0, fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>{note}</p>
          {/* 廃番ありで留まったとき＝カートに有効品が入っているので「注文する画面へ」を出す */}
          {cart.lines(order.tenant_id).length > 0 && (
            <button
              type="button"
              data-testid={`reorder-proceed-${order.order_id}`}
              onClick={() => nav("/catalog")}
              className="kz-btn kz-btn--primary kz-pressable"
              style={{ marginTop: "var(--kz-space-2)", minHeight: 44 }}
            >
              注文する画面へ
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        data-testid={`reorder-${order.order_id}`}
        onClick={() => reorder.mutate()}
        disabled={reorder.isPending}
        className="kz-btn kz-btn--flat kz-pressable"
        style={{ marginTop: "var(--kz-space-3)", width: "100%", minHeight: 48 }}
      >
        同じ内容で再注文
      </button>
    </>
  );
}
